// src/pages/AdsAdminPage.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import Toast from "../components/Toast";

import Chip from "../components/ads-admin/Chip";
import Stepper from "../components/ads-admin/Stepper";
import Tip from "../components/ads-admin/Tip";
import Collapsible from "../components/ads-admin/Collapsible";
import LockedSection from "../components/ads-admin/LockedSection";
import Drawer from "../components/ads-admin/Drawer";
import FullscreenOverlay from "../components/ads-admin/FullscreenOverlay";
import ProviderCard from "../components/ads-admin/ProviderCard";
import AdsMatrixView, { AdDetailContent } from "../components/ads-admin/AdsMatrixView";
import useAdsAdminController from "../components/ads-admin/useAdsAdminController";
import AdsChatDrawer from "../components/ads-admin/AdsChatDrawer";
import AdsSettingsModal from "../components/ads-admin/AdsSettingsModal";
import CompetitorsModal from "../components/ads-admin/CompetitorsModal";
import PipelineStatusStrip from "../components/ads-admin/PipelineStatusStrip";
import { adsGetCreativeSnapshot } from "../services/api";
import { isDirectImageUrl } from "../components/ads-admin/providerMeta";

import {
  copyToClipboard,
  fmtDt,
  humanizeToastMessage,
  sanitizeSource,
  toneFromStatus,
  getBrandId,
} from "../components/ads-admin/utils";

export default function AdsAdminPage({ currentUser }) {
  const nav = useNavigate();
  const userLabel = currentUser?.full_name || currentUser?.email || "User";

  const [toast, setToast] = useState(null);
  const showToast = (message, type = "info") => setToast({ message: humanizeToastMessage(message), type });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [competitorsOpen, setCompetitorsOpen] = useState(false);
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [chatAds, setChatAds] = useState(null);       // null = closed, array = open with ads
  const [historyOpen, setHistoryOpen] = useState(false); // standalone history view
  const [showManualPaste, setShowManualPaste] = useState(false);
  const [captureProgress, setCaptureProgress] = useState(null); // null | { done, total }

  const handlePreviewSelected = async (selectedAds) => {
    const snapshotCreatives = selectedAds.flatMap(ad =>
      (ad.creatives || []).filter(c => c.media_url
        && !isDirectImageUrl(c.media_url)
        && !/\.(mp4|webm|ogg|mov)($|\?)/i.test(c.media_url))
    );
    if (snapshotCreatives.length === 0) {
      showToast("No snapshot URLs found among selected ads.", "info");
      return;
    }
    const total = snapshotCreatives.length;
    setCaptureProgress({ done: 0, total, ok: 0, failed: 0 });

    let done = 0, ok = 0, failed = 0;
    let blocked = null;   // set on the first provider refusal, which stops the batch

    // Match the server's own capture concurrency instead of firing everything
    // at once: a burst achieves nothing beyond queueing behind the same
    // semaphore, and the extra refused requests extend a block.
    const CONCURRENCY = 2;
    const queue = [...snapshotCreatives];

    const worker = async () => {
      while (queue.length && !blocked) {
        const c = queue.shift();
        try {
          await adsGetCreativeSnapshot(c.id);
          ok++;
        } catch (e) {
          failed++;
          // A provider-wide block is not this creative's fault and will hit
          // every remaining one. Stop rather than grind through the rest.
          if (e?.detail?.reason === "provider_blocked") blocked = e;
        }
        done++;
        setCaptureProgress({ done, total, ok, failed });
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
    setCaptureProgress(null);
    // Cards and list rows decide what to show from the loaded ad data; without
    // a reload they kept the placeholder until something else refreshed it.
    if (ok > 0) reloadCurrentAds?.();

    // Report what actually happened. The old message counted attempts, so a
    // fully blocked batch still claimed every screenshot had been captured.
    if (blocked) {
      const mins = Math.max(1, Math.round((blocked?.detail?.retry_after_seconds || 0) / 60));
      showToast(
        `Stopped after ${done} of ${total}: the provider is refusing captures. ` +
        `${ok} captured, ${total - done} not attempted. Try again in about ${mins} min.`,
        "warn"
      );
    } else if (failed === 0) {
      showToast(`${ok}/${total} screenshot${total !== 1 ? "s" : ""} captured.`, "success");
    } else {
      showToast(
        `${ok}/${total} captured, ${failed} failed.`,
        ok > 0 ? "warn" : "error"
      );
    }
  };

  const controller = useAdsAdminController({
    showToast,
  });

  const {
    browseRef,

    step,
    setStep,
    maxStepAllowed,

    loadingHealth,
    apiStatus,
    apiTone,

    brands,
    loadingBrands,
    brandId,
    setBrandId,
    selectedBrand,
    competitors,
    handleAddCompetitor,
    handleRemoveCompetitor,
    competitorEntities,
    loadCompetitorEntities,

    source,
    setSource,

    runsSorted,
    loadingRuns,

    logs,
    loadingLogs,

    entitiesForSource,
    loadingEntities,
    newEntityRef,
    setNewEntityRef,
    savingEntity,
    deletingEntityId,

    validatingKey,
    bulkValidating,
    bulkProgress,

    fetchDateFrom,
    setFetchDateFrom,
    fetchDateTo,
    setFetchDateTo,

    searchQ,
    setSearchQ,
    searchItems,
    loadingSearch,
    searchError,
    searchOpen,
    setSearchOpen,
    searchPage,
    setSearchPage,

    starting,
    executing,
    finalizing,

    currentRunId,
    currentRunSource,

    runStats,

    ads,
    loadingAds,
    adsLimit,
    setAdsLimit,
    adsOffset,
    adsTotal,
    adsFullscreen,
    setAdsFullscreen,

    viewMode,
    setViewMode,
    filterDateFrom, setFilterDateFrom,
    filterDateTo, setFilterDateTo,
    filterEndDateFrom, setFilterEndDateFrom,
    filterEndDateTo, setFilterEndDateTo,
    filterStatus, setFilterStatus,
    filterCreativeType, setFilterCreativeType,
    filterHeadline, setFilterHeadline,
    filterAdvertiser, setFilterAdvertiser,
    filterAdvertiserName, setFilterAdvertiserName,
    filterCta, setFilterCta,
    filterBody, setFilterBody,
    filterVisualPalette, setFilterVisualPalette,
    filterVisualFunnel, setFilterVisualFunnel,
    filterVisualClaim, setFilterVisualClaim,
    filterPeoplePresent, setFilterPeoplePresent,
    filterNewDays, setFilterNewDays,
    filterPublisherPlatform, setFilterPublisherPlatform,
    filterLanguage, setFilterLanguage,
    filterTargetGender, setFilterTargetGender,
    filterEuReachMin, setFilterEuReachMin,
    filterTargetDomain, setFilterTargetDomain,
    filterGooglePlatform, setFilterGooglePlatform,
    fieldFilters, setFieldFilters,
    filterAsOf, setFilterAsOf,
    filterNotUpdatedSince, setFilterNotUpdatedSince,
    filterRunningFrom, setFilterRunningFrom,
    filterRunningTo, setFilterRunningTo,

    filterIngestionRunId, setFilterIngestionRunId,
    applyFilters,
    clearFilters,
    savedFilters,
    saveCurrentFilter,
    deleteSavedFilter,
    applySavedFilter,

    drawerOpen,
    setDrawerOpen,
    selectedAdId,
    setSelectedAdId,
    selectedAd,
    setSelectedAd,
    loadingAd,

    showAdvancedRuns,
    setShowAdvancedRuns,
    showAdvancedLogs,
    setShowAdvancedLogs,
    showFinalize,
    setShowFinalize,

    activeEntities,
    validatedActiveEntities,
    canProceedStep2,
    canProceedStep3,
    canProceedStep4,
    hasSuccessfulRunForSource,

    refreshAll,
    loadAds,
    loadEntities,
    runSearch,
    upsertEntity,
    deleteEntity,
    validateEntity,
    bulkValidate,
    executeRunById,
    runNowCreateAndExecute,
    startIngestionOnly,
    finalizeRun,
    openAdDrawer,
    reloadCurrentAds,
    exportCsv,
    nextPage,
    prevPage,
    setAdsOffset,
    deleteAds,
    ingestCompetitors,
    setIngestCompetitors,

    providers,
    providerSnapshot,
    selectProviderAnd,
    lastRunForSource,
  } = controller;

  // Jump from a run to exactly the ads it contributed. This is the clearest
  // place the collection/run distinction becomes concrete: the list stops
  // showing the brand's whole history and shows one run's net new ads.
  const showRunContribution = (run) => {
    setFilterIngestionRunId(String(run.id));
    setStep(4);
    applyFilters({ firstIngestionRunId: String(run.id) });
    showToast(
      run.ads_added != null
        ? `Showing the ${run.ads_added} ads run #${run.id} added to storage.`
        : `Showing the ads run #${run.id} added to storage.`,
      "info",
    );
  };

  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const searchResultsRef = useRef(null);

  useEffect(() => {
    if (step === 2 && step2Ref.current) {
      step2Ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (step === 3 && step3Ref.current) {
      step3Ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  useEffect(() => {
    if (!loadingSearch && searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [loadingSearch]);

  const styles = {
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
      gap: 14,
      marginTop: 18,
    },
    gridWide: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 14,
      marginTop: 18,
    },
    label: { fontSize: 12, fontWeight: 800, color: "#374151" },
    select: {
      width: "100%",
      marginTop: 6,
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(15,23,42,0.14)",
      outline: "none",
      background: "#fff",
      color: "#111827",
      fontSize: 14,
      fontWeight: 700,
    },
    input: {
      width: "100%",
      marginTop: 6,
      padding: "10px 12px",
      borderRadius: 12,
      border: "1px solid rgba(15,23,42,0.14)",
      outline: "none",
      background: "#fff",
      color: "#111827",
      fontSize: 14,
      fontWeight: 700,
    },
    divider: { height: 1, background: "rgba(15,23,42,0.08)", margin: "12px 0" },
    btnRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 },
    mono: {
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 12,
      color: "#0f172a",
      fontWeight: 800,
    },
    small: { fontSize: 12, color: "#6b7280", fontWeight: 650, lineHeight: 1.4 },
    list: { display: "grid", gap: 10, marginTop: 12 },
    listItem: {
      border: "1px solid rgba(15,23,42,0.10)",
      borderRadius: 14,
      padding: 12,
      background: "#fff",
      display: "grid",
      gap: 8,
    },
    miniTable: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: 10,
      fontSize: 13,
    },
    th: {
      textAlign: "left",
      padding: "10px 10px",
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.2,
      background: "#0f172a",
      color: "#f9fafb",
      borderBottom: "1px solid rgba(255,255,255,0.12)",
      fontWeight: 900,
      whiteSpace: "nowrap",
    },
    td: {
      padding: "10px 10px",
      borderBottom: "1px solid rgba(15,23,42,0.08)",
      color: "#111827",
      fontWeight: 650,
      verticalAlign: "middle",
    },
  };

  // ------------------------
  // Confidence helpers (Step 2 search results)
  // ------------------------
  const fmtConfidence = (c) => {
    const n = Number(c);
    if (!Number.isFinite(n)) return "-";
    return `${Math.round(n * 100)}%`;
  };

  const confidenceTone = (c) => {
    const n = Number(c);
    if (!Number.isFinite(n)) return "neutral";
    if (n >= 0.85) return "success";
    if (n >= 0.65) return "info";
    if (n >= 0.45) return "warn";
    return "neutral";
  };

  const providerHint = useMemo(() => {
    const s = sanitizeSource(source);
    if (s === "meta") return "Meta expects a numeric Page/advertiser ID. Use search to avoid copy/paste mistakes.";
    if (s === "google") return "If search returns 0: try lowercase, remove 'www', or use only domain/token (netflix.com).";
    if (s === "tiktok") return "TikTok lists advertisers by legal name (e.g. LIDL ROMANIA SRL, DANTE INTERNATIONAL S.A. for eMAG). Add from search: TikTok needs the exact name stored with the ID.";
    return "Add advertiser IDs that match provider expectations.";
  }, [source]);

  const onStepBlocked = (targetStep) => {
    if (targetStep === 2) return showToast("Pick a brand and provider first (Step 1).", "warn");
    if (targetStep === 3)
      return showToast("Add and verify at least one advertiser account first (Step 2).", "warn");
    if (targetStep === 4) return showToast("Fetch ads at least once successfully first (Step 3).", "warn");
    return showToast("Complete the previous steps first.", "warn");
  };

  const gotoAdvertisersFor = (src) => {
    selectProviderAnd(src, () => {
      setStep(2);
      const name = selectedBrand?.name ? String(selectedBrand.name) : "";
      if (name) setSearchQ((prev) => prev || name);
    });
  };

  const runNowFor = (src) => {
    selectProviderAnd(src, () => {
      setStep(3);
      setTimeout(() => runNowCreateAndExecute(), 0);
    });
  };

  const browseFor = (src) => {
    selectProviderAnd(src, () => {
      setStep(4);
      setTimeout(() => browseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    });
  };

  const step2Locked = step < 2;
  const step2Reason = !canProceedStep2 ? "Pick a brand and provider first (Step 1)." : "Complete Step 1 first.";

  const step3Locked = step < 3;
  const step3Reason = !canProceedStep3
    ? "Add and verify at least one advertiser account first (Step 2)."
    : "Complete Step 2 first.";

  const step4Locked = step < 4;
  const step4Reason = !canProceedStep4 ? "Fetch ads at least once successfully first (Step 3)." : "Complete Step 3 first.";

  const SEARCH_PAGE_SIZE = 10;
  const searchTotal = (searchItems || []).length;
  const searchTotalPages = Math.max(1, Math.ceil(searchTotal / SEARCH_PAGE_SIZE));
  const searchPageSafe = Math.min(searchPage, searchTotalPages - 1);
  const searchSlice = (searchItems || []).slice(
    searchPageSafe * SEARCH_PAGE_SIZE,
    searchPageSafe * SEARCH_PAGE_SIZE + SEARCH_PAGE_SIZE
  );

  // ------------------------
  // Step 4 - AdsMatrixView props (shared between inline and fullscreen)
  // ------------------------
  const matrixViewProps = {
    ads,
    loadingAds,
    adsTotal,
    adsOffset,
    adsLimit,
    nextPage,
    prevPage,
    onOffsetChange: setAdsOffset,
    viewMode,
    setViewMode,
    filterDateFrom, setFilterDateFrom,
    filterDateTo, setFilterDateTo,
    filterEndDateFrom, setFilterEndDateFrom,
    filterEndDateTo, setFilterEndDateTo,
    filterStatus, setFilterStatus,
    filterCreativeType, setFilterCreativeType,
    filterHeadline, setFilterHeadline,
    filterAdvertiser, setFilterAdvertiser,
    filterAdvertiserName, setFilterAdvertiserName,
    filterCta, setFilterCta,
    filterBody, setFilterBody,
    filterVisualPalette, setFilterVisualPalette,
    filterVisualFunnel, setFilterVisualFunnel,
    filterVisualClaim, setFilterVisualClaim,
    filterPeoplePresent, setFilterPeoplePresent,
    filterNewDays, setFilterNewDays,
    filterPublisherPlatform, setFilterPublisherPlatform,
    filterLanguage, setFilterLanguage,
    filterTargetGender, setFilterTargetGender,
    filterEuReachMin, setFilterEuReachMin,
    filterTargetDomain, setFilterTargetDomain,
    filterGooglePlatform, setFilterGooglePlatform,
    fieldFilters, setFieldFilters,
    filterAsOf, setFilterAsOf,
    filterNotUpdatedSince, setFilterNotUpdatedSince,
    filterRunningFrom, setFilterRunningFrom,
    filterRunningTo, setFilterRunningTo,
    applyFilters,
    clearFilters,
    savedFilters,
    onSaveFilter: saveCurrentFilter,
    onApplySavedFilter: applySavedFilter,
    onDeleteSavedFilter: deleteSavedFilter,
    onSelectAd: openAdDrawer,
    onExportCsv: exportCsv,
    onPreviewSelected: handlePreviewSelected,
    onChatSelected: (selectedAds) => setChatAds(selectedAds),
    onDeleteAds: deleteAds,
    brandId: getBrandId(selectedBrand),
    competitors,
    source,
  };

  const AdsBrowseBlock = (
    <div>
      {/* Smart empty state */}
      {!loadingAds && adsTotal === 0 && (
        <div style={{ margin: "8px 0 14px", padding: "14px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 13, color: "#92400e", marginBottom: 6 }}>No ads found</div>
          <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6 }}>
            {!brandId
              ? "Select a brand above to get started."
              : validatedActiveEntities.length === 0
              ? <>No verified advertiser accounts yet. <button onClick={() => { setSetupExpanded(true); setStep(2); }} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800, fontSize: 12, padding: 0 }}>Add accounts →</button></>
              : !hasSuccessfulRunForSource
              ? <>Ads haven't been fetched yet. <button onClick={() => { setSetupExpanded(true); setStep(3); }} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800, fontSize: 12, padding: 0 }}>Fetch ads now →</button></>
              : "Try clearing your filters or refreshing."}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
        <div style={{ minWidth: 130 }}>
          <div style={styles.label}>Page size</div>
          <select
            style={styles.select}
            value={String(adsLimit)}
            onChange={(e) => {
              setAdsLimit(Number(e.target.value));
              controller.setAdsOffset(0);
            }}
            disabled={loadingAds || executing || !!validatingKey || bulkValidating}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <button
            className="btn-secondary"
            onClick={() => loadAds(brandId, source, adsLimit, adsOffset)}
            disabled={loadingAds || executing || !brandId}
            style={{ marginBottom: 0 }}
          >
            Refresh
          </button>

          <button className="btn-secondary" onClick={() => setAdsFullscreen(true)} disabled={!brandId}>
            ⛶ Fullscreen
          </button>

          <button
            className="btn-secondary"
            onClick={() => nav("/ads/monitors")}
            title="Scheduled competitive-intelligence monitors"
          >
            📡 Monitors
          </button>

          <button
            className="btn-secondary"
            onClick={() => nav("/ads/intelligence")}
            title="AI competitive-intelligence report from visual attributes"
          >
            🧠 Intelligence
          </button>

          <button
            className="btn-secondary"
            onClick={() => setSettingsOpen(true)}
            title="Configure AI prompts and models for this brand"
          >
            ⚙ Settings
          </button>

          <button
            className="btn-secondary"
            onClick={() => setHistoryOpen(true)}
            title="View MAD Pilot chat history for this brand"
          >
            🕐 History
          </button>
        </div>
      </div>

      <PipelineStatusStrip />

      {/* Screenshot capture progress bar */}
      {captureProgress && (
        <div style={{ marginBottom: 10, padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#0369a1", marginBottom: 4 }}>
              📸 Capturing screenshots… {captureProgress.done}/{captureProgress.total}
              {captureProgress.failed > 0 && (
                <span style={{ color: "#b45309" }}>
                  {" "}· {captureProgress.ok} captured, {captureProgress.failed} failed
                </span>
              )}
            </div>
            <div style={{ height: 6, background: "#e0f2fe", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", background: "#0284c7", borderRadius: 99, width: `${Math.round((captureProgress.done / captureProgress.total) * 100)}%`, transition: "width 0.2s ease" }} />
            </div>
          </div>
        </div>
      )}

      {/* The list normally shows the brand's whole collection. When it has been
          narrowed to one run's contribution, say so plainly - otherwise the
          smaller count reads as ads having gone missing. */}
      {filterIngestionRunId && (
        <div style={{
          marginBottom: 10, padding: "9px 13px", background: "#eef2ff",
          border: "1px solid #c7d2fe", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#3730a3" }}>
            Showing only the ads run #{filterIngestionRunId} added to storage.
          </span>
          <button
            onClick={() => { setFilterIngestionRunId(""); applyFilters({ firstIngestionRunId: undefined }); }}
            style={{
              border: "1px solid #c7d2fe", background: "#fff", color: "#4338ca",
              fontSize: 12, fontWeight: 800, borderRadius: 7,
              padding: "4px 10px", cursor: "pointer", boxShadow: "none", transform: "none",
            }}
          >
            Show everything in storage
          </button>
        </div>
      )}

      <AdsMatrixView {...matrixViewProps} />
    </div>
  );

  return (
    <AppShell
      headerSubtitle={`Ads Discovery - ${userLabel}`}
      headerRight={<UserActionsBar mode="module" currentUser={currentUser} onBackToModules={() => nav("/modules")} />}
    >
      <header className="page-header">
        <div>
          <h1 className="page-title">Ads Discovery</h1>
          <p className="page-sub">Discover and analyze competitor ads across Meta, Google, and TikTok.</p>

          <div
            style={{
              marginTop: 10,
              padding: 14,
              borderRadius: 16,
              border: "1px solid rgba(15,23,42,0.10)",
              background: "#ffffff",
              boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ display: "grid", gap: 6, minWidth: 260 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280" }}>Current scope</div>
              <div style={{ fontSize: 16, fontWeight: 950, color: "#0f172a" }}>
                {selectedBrand ? `#${getBrandId(selectedBrand) ?? selectedBrand.id} - ${selectedBrand.name}` : "No brand selected"}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <Chip tone="neutral">Provider: {sanitizeSource(source)}</Chip>
                <Chip tone={validatedActiveEntities.length > 0 ? "success" : "warn"}>Validated advertisers: {validatedActiveEntities.length}</Chip>
                {controller.lastRunForSource?.id ? (
                  <Chip tone={toneFromStatus(controller.lastRunForSource.status)}>
                    Last run #{controller.lastRunForSource.id}: {controller.lastRunForSource.status || "-"}
                  </Chip>
                ) : (
                  <Chip tone="neutral">No runs yet</Chip>
                )}
              </div>
            </div>

            <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <Chip tone={apiTone}>API: {loadingHealth ? "checking…" : apiStatus}</Chip>

              <button
                className="btn-secondary"
                onClick={refreshAll}
                disabled={loadingBrands || loadingRuns || loadingHealth || starting || executing || validatingKey || bulkValidating}
                style={{ padding: "10px 14px", borderRadius: 12 }}
              >
                {loadingBrands || loadingRuns || loadingHealth ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          <Stepper
            step={step}
            setStep={(s) => {
              if (canProceedStep4 && !setupExpanded && s < 4) setSetupExpanded(true);
              setStep(s);
            }}
            maxStepAllowed={maxStepAllowed}
            onBlocked={onStepBlocked}
          />
        </div>
      </header>

      {/* Compact status bar - shown when fully configured and setup is collapsed */}
      {canProceedStep4 && !setupExpanded && (
        <div style={{
          marginBottom: 12, padding: "12px 18px",
          background: "#f8fafc", border: "1px solid rgba(15,23,42,0.10)",
          borderRadius: 14, display: "flex", alignItems: "center", gap: 12,
          flexWrap: "wrap", boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flex: 1 }}>
            <span style={{ fontWeight: 900, fontSize: 14, color: "#111827" }}>✅ {selectedBrand?.name}</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <Chip tone="neutral">{sanitizeSource(source)}</Chip>
            <Chip tone={validatedActiveEntities.length > 0 ? "success" : "warn"}>
              {validatedActiveEntities.length} account{validatedActiveEntities.length !== 1 ? "s" : ""} verified
            </Chip>
            {lastRunForSource?.id && (
              <Chip tone={toneFromStatus(lastRunForSource.status)}>
                Last fetch: {fmtDt(lastRunForSource.completed_at || lastRunForSource.started_at)}
              </Chip>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              className="btn-secondary"
              onClick={() => { setSetupExpanded(true); setStep(3); setTimeout(() => runNowCreateAndExecute(), 0); }}
              disabled={starting || executing}
              style={{ padding: "8px 14px", borderRadius: 12 }}
              title="Start a new ingestion run to fetch latest ads"
            >
              {starting || executing ? "Fetching…" : "⟳ Fetch new ads"}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setSetupExpanded(true)}
              style={{ padding: "8px 14px", borderRadius: 12 }}
              title="Expand setup to change brand, provider, or advertiser accounts"
            >
              ↕ Setup
            </button>
          </div>
        </div>
      )}

      {/* Back to Ads button when setup is manually expanded */}
      {canProceedStep4 && setupExpanded && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button
            className="btn-primary"
            onClick={() => { setSetupExpanded(false); setStep(4); }}
            style={{ padding: "9px 18px", borderRadius: 12 }}
          >
            ← Back to Ads
          </button>
        </div>
      )}

      {/* STEPS 1–3 - hidden when configured and setup is collapsed */}
      {(!canProceedStep4 || setupExpanded) && (
      <>
      <div style={styles.gridWide}>
        <section style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 950, color: "#111827" }}>Step 1 - Brand & Source</h3>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                Choose the brand you want to monitor, then pick the ad network below.
              </p>
            </div>
            <Chip tone={step === 1 ? "info" : "neutral"}>{step === 1 ? "Current" : "-"}</Chip>
          </div>

          <div style={styles.divider} />

          <div style={{ display: "grid", gap: 16 }}>
            <div style={{ maxWidth: 420 }}>
              <div style={styles.label}>Brand</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select
                  style={{ ...styles.select, flex: 1 }}
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  disabled={loadingBrands || starting || executing || !!validatingKey || bulkValidating}
                >
                  <option value="">{loadingBrands ? "Loading…" : "Select a brand…"}</option>
                  {brands.map((b, idx) => {
                    const bid = getBrandId(b);
                    if (!bid) return null;
                    return (
                      <option key={`${bid}-${idx}`} value={String(bid)}>
                        #{bid} - {b?.name ?? "Unnamed"}
                      </option>
                    );
                  })}
                </select>
                {brandId && (
                  <button
                    className="btn-secondary"
                    onClick={() => setCompetitorsOpen(true)}
                    style={{ padding: "10px 14px", borderRadius: 12, whiteSpace: "nowrap", marginTop: 6 }}
                    title="Manage competitor brands"
                  >
                    ⚔ Competitors{competitors.length > 0 ? ` (${competitors.length})` : ""}
                  </button>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", marginBottom: 10 }}>Ad networks</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
                {providers.map((p) => {
                  const snap = providerSnapshot[p.key] || {};
                  return (
                    <ProviderCard
                      key={p.key}
                      title={p.title}
                      subtitle={p.subtitle}
                      icon={p.icon}
                      selected={sanitizeSource(source) === p.key}
                      disabled={!brandId}
                      onSelect={() => setSource(p.key)}
                      configuredTone={snap.configuredTone || "neutral"}
                      configuredLabel={snap.configuredLabel || "Unknown"}
                      activeCount={snap.activeCount || 0}
                      validatedCount={snap.validatedCount || 0}
                      lastRun={snap.lastRun || null}
                      onManageAdvertisers={() => gotoAdvertisersFor(p.key)}
                      onRunNow={() => runNowFor(p.key)}
                      onBrowseAds={() => browseFor(p.key)}
                    />
                  );
                })}
              </div>
            </div>

            {brandId && source && (
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, lineHeight: 1.5 }}>{providerHint}</div>
            )}

            <div style={styles.btnRow}>
              <button className="btn-primary" onClick={() => setStep(2)} disabled={!canProceedStep2} style={{ padding: "12px 18px", borderRadius: 14 }}>
                Continue → Accounts
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* STEP 2 */}
      <div ref={step2Ref} style={styles.grid}>
        <LockedSection locked={step2Locked} reason={step2Reason}>
          <section style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 950, color: "#111827" }}>Step 2 - Accounts</h3>
              <Chip tone={step === 2 ? "info" : step > 2 ? "success" : "neutral"}>{step === 2 ? "Current" : step > 2 ? "Done" : "-"}</Chip>
            </div>

            <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
              An advertiser account links this brand to a specific page in the ad library (e.g. a Meta Page ID).
              Add at least one account below, verify it, then continue.
            </p>

            <div style={styles.divider} />

            <div>
              <div style={styles.label}>Search for this brand's accounts</div>
              <input
                style={{ ...styles.input, background: "#f3f4f6" }}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder={`Search ${selectedBrand?.name ? `"${selectedBrand.name}"` : "brand"}…`}
                disabled={!brandId || starting || executing || !!validatingKey || bulkValidating}
              />

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                <button className="btn-secondary" onClick={() => runSearch(searchQ)} disabled={!brandId || loadingSearch || !String(searchQ || "").trim()}>
                  {loadingSearch ? "Searching…" : "Search"}
                </button>

                <button
                  className="btn-secondary"
                  onClick={() => {
                    setSearchQ(selectedBrand?.name || "");
                    controller.setSearchPage(0);
                    controller.setSearchOpen(true);
                  }}
                  disabled={!brandId}
                >
                  Reset to brand name
                </button>
              </div>

              {searchError && <div style={{ marginTop: 10, color: "#991b1b", fontWeight: 800, fontSize: 12 }}>{humanizeToastMessage(searchError)}</div>}

              <div ref={searchResultsRef} style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 900, color: "#111827", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  Search results <Chip tone="neutral">{loadingSearch ? "…" : `${searchTotal}`}</Chip>

                  <button className="btn-secondary" onClick={() => setSearchOpen((v) => !v)} style={{ marginLeft: "auto" }} disabled={loadingSearch}>
                    {searchOpen ? "Hide results" : "Show results"}
                  </button>
                </div>

                {searchOpen && (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                      <button className="btn-secondary" onClick={() => setSearchPage((p) => Math.max(0, p - 1))} disabled={loadingSearch || searchPageSafe <= 0}>
                        Prev
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setSearchPage((p) => Math.min(searchTotalPages - 1, p + 1))}
                        disabled={loadingSearch || searchPageSafe >= searchTotalPages - 1}
                      >
                        Next
                      </button>
                      <Chip tone="neutral">
                        Page {searchTotal === 0 ? 0 : searchPageSafe + 1}/{searchTotalPages}
                      </Chip>
                    </div>

                    {searchTotal === 0 && !loadingSearch && <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>No results yet.</div>}

                    <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                      {searchSlice.map((it, idx) => {
                        const adv = String(it?.advertiser_ref || it?.id || it?.ref || "").trim();
                        const label = it?.label || it?.name || it?.title || adv || "Unknown";
                        const already = entitiesForSource.some((e) => String(e?.advertiser_ref || "") === adv);
                        const conf = it?.confidence;

                        return (
                          <div
                            key={`${adv || "x"}-${searchPageSafe}-${idx}`}
                            style={{
                              border: "1px solid rgba(15,23,42,0.10)",
                              borderRadius: 14,
                              padding: 12,
                              background: "#fff",
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              alignItems: "flex-start",
                            }}
                          >
                            <div style={{ display: "grid", gap: 6 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
                                <Chip tone={confidenceTone(conf)}>Confidence: {fmtConfidence(conf)}</Chip>
                              </div>

                              <div style={styles.small}>
                                Account ID: <span style={styles.mono}>{adv || "-"}</span>
                              </div>

                              {it?.extra?.target_domain && (
                                <div style={styles.small}>
                                  Domain: <span style={styles.mono}>{String(it.extra.target_domain)}</span>
                                </div>
                              )}
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              <button
                                className="btn-secondary"
                                onClick={async () => {
                                  const ok = await copyToClipboard(adv);
                                  showToast(ok ? "Copied advertiser reference." : "Could not copy.", ok ? "success" : "error");
                                }}
                                disabled={!adv}
                              >
                                Copy
                              </button>

                              <button className="btn-primary" onClick={() => upsertEntity(adv, true, label, it?.extra?.target_domain || it?.extra?.resolved_domain)} disabled={!adv || savingEntity || already}>
                                {already ? "Added" : "Add & activate"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div style={styles.divider} />

            <div>
              <button
                className="btn-secondary"
                onClick={() => setShowManualPaste((v) => !v)}
                style={{ fontSize: 12, padding: "6px 12px", borderRadius: 10 }}
              >
                {showManualPaste ? "▲ Hide" : "▼ Add by ID manually"}
              </button>

              {showManualPaste && (
                <div style={{ marginTop: 10 }}>
                  <div style={styles.label}>Account ID</div>
                  <input style={styles.input} value={newEntityRef} onChange={(e) => setNewEntityRef(e.target.value)} disabled={!brandId || savingEntity} placeholder="e.g. 123456789" />

                  <div style={styles.btnRow}>
                    <button className="btn-primary" onClick={() => upsertEntity(newEntityRef, true)} disabled={!brandId || savingEntity || !String(newEntityRef || "").trim()}>
                      {savingEntity ? "Saving…" : "Add & activate"}
                    </button>

                    <button className="btn-secondary" onClick={() => loadEntities(brandId, null)} disabled={!brandId || loadingEntities}>
                      Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.divider} />

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div style={{ fontWeight: 900, color: "#111827" }}>
                🔵 {selectedBrand?.name || "Primary"} - accounts ({sanitizeSource(source)})
              </div>
              <Chip tone={validatedActiveEntities.length > 0 ? "success" : "warn"}>
                {loadingEntities ? "loading…" : `${validatedActiveEntities.length} validated / ${activeEntities.length} active / ${entitiesForSource.length} total`}
              </Chip>
            </div>

            <div style={styles.btnRow}>
              <button className="btn-secondary" onClick={() => bulkValidate({ onlyUnvalidated: true })} disabled={!brandId || bulkValidating || loadingEntities}>
                {bulkValidating ? `Verifying… (${bulkProgress.done}/${bulkProgress.total})` : "Verify new accounts"}
              </button>

              <button className="btn-secondary" onClick={() => bulkValidate({ onlyUnvalidated: false })} disabled={!brandId || bulkValidating || loadingEntities}>
                {bulkValidating ? `Verifying… (${bulkProgress.done}/${bulkProgress.total})` : "Verify all accounts"}
              </button>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.miniTable}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 70 }}>ID</th>
                    <th style={styles.th}>Account</th>
                    <th style={{ ...styles.th, width: 120 }}>Active</th>
                    <th style={{ ...styles.th, width: 160 }}>Verify</th>
                    <th style={{ ...styles.th, width: 140 }}>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {(entitiesForSource || []).map((e) => {
                    const adv = String(e?.advertiser_ref || "");
                    const key = `${sanitizeSource(source)}:${adv}`;
                    const isValidated = e?.last_validation_ok === true;

                    return (
                      <tr key={e.id}>
                        <td style={{ ...styles.td, fontWeight: 900 }}>{e.id}</td>
                        <td style={styles.td}>
                          <div style={{ display: "grid", gap: 6 }}>
                            <div style={{ fontWeight: 900, color: "#0f172a" }}>{e?.label || "-"}</div>
                            <span style={styles.mono}>{adv || "-"}</span>
                            <Chip tone={isValidated ? "success" : "neutral"}>{isValidated ? "Validated" : "Not validated"}</Chip>
                          </div>
                        </td>

                        <td style={styles.td}>
                          <button className="btn-secondary" onClick={() => upsertEntity(e.advertiser_ref, !e.active)} disabled={savingEntity || starting || executing}>
                            {e.active ? "Active ✅" : "Inactive ⛔"}
                          </button>
                        </td>

                        <td style={styles.td}>
                          <button className="btn-secondary" onClick={() => validateEntity(e.advertiser_ref)} disabled={!brandId || savingEntity || bulkValidating || validatingKey === key}>
                            {validatingKey === key ? "Verifying…" : "Verify"}
                          </button>
                        </td>

                        <td style={styles.td}>
                          <button className="btn-secondary" onClick={() => deleteEntity(e.id)} disabled={deletingEntityId === e.id || savingEntity || starting || executing}>
                            {deletingEntityId === e.id ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {!loadingEntities && (entitiesForSource || []).length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...styles.td, color: "#6b7280" }}>
                        No advertisers yet for this provider. Add at least one above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Competitor brand entity sections */}
            {competitors.map((c) => {
              const cid = c.competitor_brand_id;
              const cEntities = (competitorEntities[cid] || []).filter(
                e => !source || sanitizeSource(e.source) === sanitizeSource(source)
              );
              return (
                <React.Fragment key={cid}>
                  <div style={styles.divider} />
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                    <div style={{ fontWeight: 900, color: "#111827" }}>
                      ⚔ {c.competitor_brand_name} - accounts ({sanitizeSource(source)})
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase" }}>Competitor</span>
                    </div>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8 }}
                      onClick={() => loadCompetitorEntities([c])}
                    >
                      Refresh
                    </button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={styles.miniTable}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: 70 }}>ID</th>
                          <th style={styles.th}>Account</th>
                          <th style={{ ...styles.th, width: 120 }}>Active</th>
                          <th style={{ ...styles.th, width: 160 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cEntities.map((e) => {
                          const isValidated = e?.last_validation_ok === true;
                          return (
                            <tr key={e.id}>
                              <td style={{ ...styles.td, fontWeight: 900 }}>{e.id}</td>
                              <td style={styles.td}>
                                <div style={{ display: "grid", gap: 4 }}>
                                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{e?.label || "-"}</div>
                                  <span style={styles.mono}>{e?.advertiser_ref || "-"}</span>
                                </div>
                              </td>
                              <td style={styles.td}>
                                <Chip tone={e.active ? "success" : "neutral"}>{e.active ? "Active" : "Inactive"}</Chip>
                              </td>
                              <td style={styles.td}>
                                <Chip tone={isValidated ? "success" : "neutral"}>{isValidated ? "Validated" : "Not validated"}</Chip>
                              </td>
                            </tr>
                          );
                        })}
                        {cEntities.length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ ...styles.td, color: "#6b7280" }}>
                              No accounts for this competitor yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </React.Fragment>
              );
            })}

            <div style={styles.btnRow}>
              <button className="btn-primary" onClick={() => setStep(3)} disabled={!canProceedStep3}>
                Continue → Fetch Ads
              </button>
            </div>
          </section>
        </LockedSection>

        {/* STEP 3 */}
        <div ref={step3Ref}>
        <LockedSection locked={step3Locked} reason={step3Reason}>
          <section style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 950, color: "#111827" }}>Step 3 - Fetch Ads</h3>
              <Chip tone={step === 3 ? "info" : step > 3 ? "success" : "neutral"}>{step === 3 ? "Current" : step > 3 ? "Done" : "-"}</Chip>
            </div>

            <Tip>
              <b>What happens:</b> clicking "Fetch ads now" starts a background job. The run ID appears immediately; status updates as it progresses.
            </Tip>

            <div style={styles.divider} />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <Chip tone="neutral">
                Active advertisers: <span style={{ fontWeight: 950 }}>{activeEntities.length}</span>
              </Chip>
              <Chip tone={validatedActiveEntities.length > 0 ? "success" : "warn"}>
                Validated active: <span style={{ fontWeight: 950 }}>{validatedActiveEntities.length}</span>
              </Chip>

              {currentRunId ? (
                <Chip tone="info">
                  Current run: #{currentRunId} ({sanitizeSource(currentRunSource)})
                </Chip>
              ) : lastRunForSource?.id ? (
                <Chip tone={toneFromStatus(lastRunForSource?.status)}>
                  Last run #{lastRunForSource.id}: {lastRunForSource.status || "-"}
                </Chip>
              ) : (
                <Chip tone="neutral">No runs yet</Chip>
              )}
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, alignItems: "flex-end" }}>
              <div>
                <div style={styles.label}>Ads active from (optional)</div>
                <input
                  type="date"
                  style={{ ...styles.input, width: 160 }}
                  value={fetchDateFrom}
                  onChange={(e) => setFetchDateFrom(e.target.value)}
                  disabled={starting || executing}
                  title="Only fetch ads that started running on or after this date"
                />
              </div>
              <div>
                <div style={styles.label}>Ads active to (optional)</div>
                <input
                  type="date"
                  style={{ ...styles.input, width: 160 }}
                  value={fetchDateTo}
                  onChange={(e) => setFetchDateTo(e.target.value)}
                  disabled={starting || executing}
                  title="Only fetch ads that started running on or before this date"
                />
              </div>
              {competitors.length > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={ingestCompetitors}
                    onChange={(e) => setIngestCompetitors(e.target.checked)}
                    disabled={starting || executing}
                  />
                  Include competitor ads ({competitors.length} competitor{competitors.length !== 1 ? "s" : ""})
                </label>
              )}
              <button className="btn-primary" onClick={runNowCreateAndExecute} disabled={!canProceedStep3 || starting || executing} style={{ marginBottom: 0 }}>
                {starting || executing ? "Fetching…" : "Fetch ads now"}
              </button>
            </div>

            <div style={styles.divider} />

            {/* ── Live ingestion progress ── */}
            {(executing || starting) && currentRunId ? (
              <div style={{ marginTop: 4, padding: "12px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10 }}>
                <div style={{ fontWeight: 900, fontSize: 13, color: "#0369a1", marginBottom: 8 }}>
                  Fetching ads…
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Chip tone="info">Ads fetched: {runStats?.stats?.raw_count ?? "…"}</Chip>
                  <Chip tone="info">Ads:{runStats?.stats?.ads_count ?? "…"}</Chip>
                  <Chip tone="info">Creatives: {runStats?.stats?.creatives_count ?? "…"}</Chip>
                </div>
              </div>
            ) : runStats ? (
              (() => {
                const s = runStats.stats || {};
                const statusTone = runStats.status === "success" ? "success" : runStats.status === "failed" ? "error" : "neutral";
                const rows = [
                  { label: "Status", value: runStats.status || "-" },
                  { label: "API items received", value: s.fetched_items ?? s.raw_count ?? "-" },
                  { label: "New ads", value: s.raw_inserted ?? s.raw_count ?? "-" },
                  { label: "Already in library", value: s.raw_duplicates ?? "-" },
                  { label: "Ads saved", value: s.ads_upserted ?? s.ads_count ?? "-" },
                  { label: "Creatives", value: s.creatives_upserted ?? s.creatives_count ?? "-" },
                ];
                return (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 8 }}>
                      <div style={{ fontWeight: 900, color: "#111827", fontSize: 13 }}>Last run results</div>
                      <Chip tone={statusTone}>{runStats.status || "-"}</Chip>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                      {rows.map(({ label, value }) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, borderBottom: "1px solid rgba(15,23,42,0.06)", paddingBottom: 4 }}>
                          <span style={{ color: "#6b7280", fontWeight: 700 }}>{label}</span>
                          <span style={{ fontWeight: 900, color: "#111827" }}>{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>No run stats yet.</div>
            )}

            <div style={styles.btnRow}>
              <button className="btn-primary" onClick={() => { setStep(4); setSetupExpanded(false); }} disabled={!canProceedStep4}>
                Browse Ads →
              </button>
            </div>

            <div style={styles.divider} />

            <Collapsible
              title="Developer tools"
              subtitle="Debug actions - not needed for normal use."
              open={showFinalize}
              setOpen={setShowFinalize}
              right={<Chip tone="neutral">{showFinalize ? "open" : "closed"}</Chip>}
            >
              <div style={styles.btnRow}>
                <button className="btn-secondary" onClick={startIngestionOnly} disabled={!canProceedStep3 || starting || executing}>
                  {starting ? "Creating…" : "Create run only"}
                </button>

                <button className="btn-secondary" onClick={() => executeRunById(lastRunForSource?.id)} disabled={!lastRunForSource?.id || executing || starting}>
                  {executing ? "Executing…" : "Execute last run"}
                </button>

                <button className="btn-secondary" onClick={() => finalizeRun("success")} disabled={!lastRunForSource?.id || finalizing || executing}>
                  {finalizing ? "Finalizing…" : "Finalize success"}
                </button>
                <button className="btn-secondary" onClick={() => finalizeRun("failed")} disabled={!lastRunForSource?.id || finalizing || executing}>
                  {finalizing ? "Finalizing…" : "Finalize failed"}
                </button>
              </div>
            </Collapsible>
          </section>
        </LockedSection>
        </div>
      </div>
      </>
      )}

      {/* STEP 4 - always rendered (locked via LockedSection when not ready) */}
      {canProceedStep4 ? (
        <section ref={browseRef} style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
          {AdsBrowseBlock}
        </section>
      ) : (
        <LockedSection locked={step4Locked} reason={step4Reason}>
          <section ref={browseRef} style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(15,23,42,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 950, color: "#111827" }}>Step 4 - Browse Ads</h3>
              <Chip tone={step === 4 ? "info" : "neutral"}>{step === 4 ? "Current" : "-"}</Chip>
            </div>
            <div style={styles.divider} />
            {AdsBrowseBlock}
          </section>
        </LockedSection>
      )}

      {/* Advanced runs */}
        <Collapsible
          title="Advanced: recent runs"
          subtitle="History table - useful for debugging and verifying scheduling."
          open={showAdvancedRuns}
          setOpen={setShowAdvancedRuns}
          right={<Chip tone="neutral">{loadingRuns ? "…" : `${runsSorted.length} total`}</Chip>}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={styles.miniTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 70 }}>ID</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Started</th>
                  <th style={styles.th}>Completed</th>
                  <th style={styles.th} title="Ads the provider returned for this run's criteria">Fetched</th>
                  <th style={styles.th} title="Ads this run put into storage. The rest were already stored.">Added to storage</th>
                  <th style={styles.th}>Creatives</th>
                  <th style={{ ...styles.th, width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(runsSorted || []).slice(0, 12).map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...styles.td, fontWeight: 900 }}>
                      {r.id}
                      {/* A refetch updates one ad already held, so its "0 added"
                          is expected; a monitor run was not started by hand. */}
                      {r.kind === "refetch" && (
                        <span title={r.target_ad_id ? `Refetch of ad #${r.target_ad_id}` : "Refetch of one ad"}
                              style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: "#0e7490", background: "#ecfeff", borderRadius: 5, padding: "1px 6px" }}>
                          refetch{r.target_ad_id ? ` · ad #${r.target_ad_id}` : ""}
                        </span>
                      )}
                      {r.kind === "monitor" && (
                        <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: "#6d28d9", background: "#f5f3ff", borderRadius: 5, padding: "1px 6px" }}>
                          monitor
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.mono}>{r.source || "-"}</span>
                    </td>
                    <td style={styles.td}>
                      <Chip tone={toneFromStatus(r.status)}>{r.status || "-"}</Chip>
                    </td>
                    <td style={styles.td}>{fmtDt(r.started_at)}</td>
                    <td style={styles.td}>{fmtDt(r.completed_at)}</td>
                    <td style={styles.td}>
                      {String(r.fetched_items ?? "-")}
                      {r.truncated && (
                        <span title="Stopped at the page limit while more results were available"
                              style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: "#b45309" }}>
                          capped
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {r.ads_added > 0 ? (
                        <button
                          onClick={() => showRunContribution(r)}
                          title={`Show the ${r.ads_added} ads this run added`}
                          style={{
                            border: "none", background: "none", padding: 0,
                            color: "#4338ca", fontWeight: 900, cursor: "pointer", boxShadow: "none", transform: "none",
                            textDecoration: "underline", fontSize: "inherit",
                          }}
                        >
                          {r.ads_added}
                        </button>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>0</span>
                      )}
                      {r.ads_upserted > (r.ads_added ?? 0) && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: "#9ca3af" }}
                              title="Already in storage, so not added again">
                          {r.ads_upserted - (r.ads_added ?? 0)} already held
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>{String(r.creatives_upserted ?? "-")}</td>
                    <td style={styles.td}>
                      <button className="btn-secondary" onClick={() => executeRunById(r.id)} disabled={executing || starting}>
                        Execute
                      </button>
                    </td>
                  </tr>
                ))}
                {!loadingRuns && (runsSorted || []).length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ ...styles.td, color: "#6b7280" }}>
                      No runs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Collapsible>

        {/* Advanced logs */}
        <Collapsible
          title="Advanced: logs"
          subtitle="Black box recorder - first stop when ingestion fails."
          open={showAdvancedLogs}
          setOpen={setShowAdvancedLogs}
          right={<Chip tone="neutral">{loadingLogs ? "…" : `${(logs || []).length}`}</Chip>}
        >
          <div style={{ display: "grid", gap: 8 }}>
            {(logs || []).slice(-12).reverse().map((l) => (
              <div
                key={l.id}
                style={{
                  border: "1px solid rgba(15,23,42,0.10)",
                  borderRadius: 12,
                  padding: 10,
                  background: "#fff",
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <Chip tone={l.level === "error" ? "error" : l.level === "warn" ? "warn" : "neutral"}>{String(l.level || "info").toUpperCase()}</Chip>
                  <span style={styles.small}>{fmtDt(l.created_at)}</span>
                </div>
                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800 }}>{l.message}</div>
              </div>
            ))}

            {!loadingLogs && (logs || []).length === 0 && <div style={{ fontSize: 12, color: "#6b7280" }}>No logs yet.</div>}
          </div>
        </Collapsible>

      {/* Fullscreen overlay - same AdsMatrixView, same state */}
      <FullscreenOverlay
        open={adsFullscreen}
        title={`Ads Browse - ${selectedBrand?.name || "Brand"} (${sanitizeSource(source)})`}
        onClose={() => setAdsFullscreen(false)}
      >
        {AdsBrowseBlock}
      </FullscreenOverlay>

      {/* Ad detail drawer - uses AdDetailContent from AdsMatrixView */}
      <Drawer
        open={drawerOpen}
        title={selectedAdId ? `Ad #${selectedAdId}` : "Ad"}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedAdId(null);
          setSelectedAd(null);
        }}
      >
        <AdDetailContent
          key={selectedAdId}
          ad={selectedAd}
          loading={loadingAd}
          brandId={getBrandId(selectedBrand)}
          onRefetched={reloadCurrentAds}
          onShowRun={(runId) => {
            setDrawerOpen(false);
            setSelectedAdId(null);
            setSelectedAd(null);
            showRunContribution({ id: runId, ads_added: null });
          }}
        />
      </Drawer>

      <AdsChatDrawer
        open={!!chatAds}
        onClose={() => setChatAds(null)}
        ads={chatAds || []}
        brandId={getBrandId(selectedBrand)}
      />
      <AdsChatDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        ads={[]}
        brandId={getBrandId(selectedBrand)}
        initialView="history"
      />

      <AdsSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        brandId={getBrandId(selectedBrand)}
      />

      <CompetitorsModal
        open={competitorsOpen}
        onClose={() => setCompetitorsOpen(false)}
        primaryBrand={selectedBrand}
        brands={brands}
        competitors={competitors}
        onAdd={handleAddCompetitor}
        onRemove={handleRemoveCompetitor}
      />

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </AppShell>
  );
}